/**
 * Unified admin flight booking hub — list + detail drawer (OTA ops workspace).
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Search, Download, Eye, RefreshCw, Plane, ChevronDown, ChevronUp } from 'lucide-react';
import { SupplierStatusBadge } from '../../components/admin/flight/SupplierStatusPanel';
import BookingDetailDrawer from '../../components/admin/flight/BookingDetailDrawer';
import TripjackCancelModal from '../../components/TripjackCancelModal';
import RiyaCancelModal from '../../components/RiyaCancelModal';
import RiyaRescheduleModal from '../../components/RiyaRescheduleModal';
import RiyaSSRModal from '../../components/RiyaSSRModal';
import ConfirmHoldModal from '../../components/ConfirmHoldModal';
import { fmtISTShort } from '../../utils/datetimeIST';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fetchOpts = { credentials: 'include' };
const fmtDate = fmtISTShort;

export default function FlightBookingHub() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [userTypeFilter, setUserTypeFilter] = useState('');
  const [paymentModeFilter, setPaymentModeFilter] = useState('');
  const [pnrFilter, setPnrFilter] = useState('');
  const [hasPnrFilter, setHasPnrFilter] = useState('');
  const [amountMin, setAmountMin] = useState('');
  const [amountMax, setAmountMax] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [travelFrom, setTravelFrom] = useState('');
  const [travelTo, setTravelTo] = useState('');
  const [clientNameFilter, setClientNameFilter] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [hub, setHub] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [tjCancelOpen, setTjCancelOpen] = useState(false);
  const [tjCancelBookingId, setTjCancelBookingId] = useState(null);
  const [riyaCancelOpen, setRiyaCancelOpen] = useState(false);
  const [riyaCancelBooking, setRiyaCancelBooking] = useState(null);
  const [riyaCancelMode, setRiyaCancelMode] = useState('cancel');
  const [riyaRescheduleBooking, setRiyaRescheduleBooking] = useState(null);
  const [riyaSSRBooking, setRiyaSSRBooking] = useState(null);
  const [confirmHoldOpen, setConfirmHoldOpen] = useState(false);
  const [confirmHoldBooking, setConfirmHoldBooking] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '20' });
      if (statusFilter) params.append('status', statusFilter);
      if (supplierFilter) params.append('supplier', supplierFilter);
      if (userTypeFilter) params.append('user_type', userTypeFilter);
      if (paymentModeFilter) params.append('payment_mode', paymentModeFilter);
      if (pnrFilter) params.append('pnr', pnrFilter);
      if (hasPnrFilter === 'yes') params.append('has_pnr', 'true');
      if (hasPnrFilter === 'no') params.append('has_pnr', 'false');
      if (amountMin) params.append('amount_min', amountMin);
      if (amountMax) params.append('amount_max', amountMax);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (travelFrom) params.append('travel_from', travelFrom);
      if (travelTo) params.append('travel_to', travelTo);
      if (clientNameFilter) params.append('client_name', clientNameFilter);
      if (search) params.append('search', search);
      const r = await fetch(`${API}/admin/flights/bookings?${params}`, fetchOpts);
      if (!r.ok) throw new Error('Failed to load');
      const d = await r.json();
      setBookings(d.bookings || []);
      setPages(d.pages || 1);
    } catch (e) {
      console.error(e);
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [
    page, statusFilter, supplierFilter, userTypeFilter, paymentModeFilter,
    pnrFilter, hasPnrFilter, amountMin, amountMax, fromDate, toDate,
    travelFrom, travelTo, clientNameFilter, search,
  ]);

  useEffect(() => { fetchList(); }, [fetchList]);

  const openDetail = async (bookingId) => {
    try {
      const r = await fetch(`${API}/admin/flights/bookings/${encodeURIComponent(bookingId)}`, fetchOpts);
      if (!r.ok) throw new Error('Detail failed');
      const d = await r.json();
      setHub(d);
      setDrawerOpen(true);
    } catch (e) {
      alert(e.message);
    }
  };

  const booking = hub?.booking || {};
  const actions = hub?.allowed_actions || [];
  const bid = hub?.keys?.canonical_id || booking.booking_id || booking.id;

  const actionButtons = drawerOpen && hub ? (
    <div className="flex flex-wrap gap-2 border-t pt-4">
      {actions.includes('tripjack_cancel') && (
        <button type="button" className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg"
          onClick={() => { setTjCancelBookingId(bid); setTjCancelOpen(true); }}>
          Tripjack Cancel
        </button>
      )}
      {actions.includes('riya_cancel') && (
        <button type="button" className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg"
          onClick={() => { setRiyaCancelMode('cancel'); setRiyaCancelBooking(booking); setRiyaCancelOpen(true); }}>
          Riya Cancel
        </button>
      )}
      {actions.includes('tripjack_refresh') && (
        <button type="button" className="px-4 py-2 bg-slate-700 text-white text-sm rounded-lg"
          onClick={async () => {
            await fetch(`${API}/admin/bookings/${bid}/tripjack/refresh`, { method: 'POST', ...fetchOpts });
            openDetail(bid);
            fetchList();
          }}>
          Refresh supplier
        </button>
      )}
      {actions.includes('riya_reschedule') && (
        <button type="button" className="px-4 py-2 bg-indigo-600 text-white text-sm rounded-lg"
          onClick={() => setRiyaRescheduleBooking(booking)}>Reschedule</button>
      )}
      {actions.includes('riya_ssr') && (
        <button type="button" className="px-4 py-2 bg-emerald-700 text-white text-sm rounded-lg"
          onClick={() => setRiyaSSRBooking(booking)}>Add extras</button>
      )}
      {actions.includes('confirm_hold') && (
        <button type="button" className="px-4 py-2 bg-emerald-600 text-white text-sm rounded-lg"
          onClick={() => { setConfirmHoldBooking(booking); setConfirmHoldOpen(true); }}>Confirm hold</button>
      )}
      {actions.includes('offline_cancel') && (
        <button type="button" className="px-4 py-2 bg-red-600 text-white text-sm rounded-lg"
          onClick={async () => {
            const reason = prompt('Cancellation reason:');
            if (!reason) return;
            const r = await fetch(`${API}/admin/bookings/${bid}/cancel?reason=${encodeURIComponent(reason)}`, {
              method: 'PUT', ...fetchOpts,
            });
            if (!r.ok) { const j = await r.json(); alert(j.detail || 'Failed'); return; }
            setDrawerOpen(false);
            fetchList();
          }}>Cancel (offline)</button>
      )}
    </div>
  ) : null;

  const clearFilters = () => {
    setUserTypeFilter('');
    setPaymentModeFilter('');
    setPnrFilter('');
    setHasPnrFilter('');
    setAmountMin('');
    setAmountMax('');
    setFromDate('');
    setToDate('');
    setTravelFrom('');
    setTravelTo('');
    setClientNameFilter('');
    setPage(1);
  };

  return (
    <div className="p-6" data-testid="flight-booking-hub">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1B375B] flex items-center gap-2">
            <Plane className="w-7 h-7" /> Flight Bookings
          </h1>
          <p className="text-sm text-gray-500">Unified hub — full booking detail, commercial data, manual ops</p>
        </div>
        <button type="button" onClick={fetchList} className="px-3 py-2 border rounded-lg text-sm font-semibold flex items-center gap-1">
          <RefreshCw className="w-4 h-4" /> Refresh
        </button>
      </div>

      <div className="bg-white rounded-lg shadow p-4 mb-4 space-y-3">
        <div className="flex flex-wrap gap-3">
          <input
            className="border rounded-lg px-3 py-2 text-sm flex-1 min-w-[200px]"
            placeholder="Search PNR, booking id, email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && (setPage(1), fetchList())}
          />
          <select className="border rounded-lg px-3 py-2 text-sm" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
            <option value="">All ACF status</option>
            <option value="confirmed">Confirmed</option>
            <option value="pending">Pending</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select className="border rounded-lg px-3 py-2 text-sm" value={supplierFilter} onChange={(e) => { setSupplierFilter(e.target.value); setPage(1); }}>
            <option value="">All suppliers</option>
            <option value="tripjack">Tripjack</option>
            <option value="riya">Riya</option>
            <option value="offline">Offline</option>
          </select>
          <button type="button" onClick={() => { setPage(1); fetchList(); }} className="px-4 py-2 bg-[#1B375B] text-white rounded-lg text-sm font-semibold flex items-center gap-1">
            <Search className="w-4 h-4" /> Search
          </button>
          <button type="button" onClick={() => setFiltersOpen((o) => !o)} className="px-3 py-2 border rounded-lg text-sm font-semibold flex items-center gap-1">
            {filtersOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            Advanced
          </button>
          <a href={`${API}/admin/bookings/export/csv`} className="px-4 py-2 bg-green-600 text-white rounded-lg text-sm flex items-center gap-1" download>
            <Download className="w-4 h-4" /> CSV
          </a>
        </div>

        {filtersOpen && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-2 border-t">
            <label className="text-xs text-gray-600">
              Agent / B2C name
              <input
                className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm"
                placeholder="Agency or client name…"
                value={clientNameFilter}
                onChange={(e) => setClientNameFilter(e.target.value)}
              />
            </label>
            <label className="text-xs text-gray-600">
              User type
              <select className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" value={userTypeFilter} onChange={(e) => setUserTypeFilter(e.target.value)}>
                <option value="">Any</option>
                <option value="agent">Agent</option>
                <option value="b2c">B2C</option>
              </select>
            </label>
            <label className="text-xs text-gray-600">
              Payment mode
              <select className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" value={paymentModeFilter} onChange={(e) => setPaymentModeFilter(e.target.value)}>
                <option value="">Any</option>
                <option value="wallet">Wallet</option>
                <option value="credit">Credit</option>
                <option value="gateway">Gateway</option>
                <option value="ccavenue">CCAvenue</option>
              </select>
            </label>
            <label className="text-xs text-gray-600">
              PNR contains
              <input className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" value={pnrFilter} onChange={(e) => setPnrFilter(e.target.value)} />
            </label>
            <label className="text-xs text-gray-600">
              Has PNR
              <select className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" value={hasPnrFilter} onChange={(e) => setHasPnrFilter(e.target.value)}>
                <option value="">Any</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </label>
            <label className="text-xs text-gray-600">
              Amount min (₹)
              <input type="number" className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" value={amountMin} onChange={(e) => setAmountMin(e.target.value)} />
            </label>
            <label className="text-xs text-gray-600">
              Amount max (₹)
              <input type="number" className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" value={amountMax} onChange={(e) => setAmountMax(e.target.value)} />
            </label>
            <label className="text-xs text-gray-600">
              Created from
              <input type="date" className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} />
            </label>
            <label className="text-xs text-gray-600">
              Created to
              <input type="date" className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" value={toDate} onChange={(e) => setToDate(e.target.value)} />
            </label>
            <label className="text-xs text-gray-600">
              Travel from
              <input type="date" className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" value={travelFrom} onChange={(e) => setTravelFrom(e.target.value)} />
            </label>
            <label className="text-xs text-gray-600">
              Travel to
              <input type="date" className="mt-1 w-full border rounded-lg px-2 py-1.5 text-sm" value={travelTo} onChange={(e) => setTravelTo(e.target.value)} />
            </label>
            <div className="col-span-2 flex items-end gap-2">
              <button type="button" onClick={() => { setPage(1); fetchList(); }} className="px-4 py-2 bg-[#1B375B] text-white rounded-lg text-sm font-semibold">Apply filters</button>
              <button type="button" onClick={clearFilters} className="px-4 py-2 border rounded-lg text-sm">Clear</button>
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Created</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Booking ID</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Agent / Client</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Supplier ID</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">PNR</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Route</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Pax</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Amount</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Payment</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Supplier status</th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {loading ? (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
            ) : bookings.length === 0 ? (
              <tr><td colSpan={11} className="px-4 py-8 text-center text-gray-500">No bookings</td></tr>
            ) : bookings.map((row) => (
              <tr key={row.booking_id} className="hover:bg-gray-50 text-sm">
                <td className="px-3 py-2 text-xs text-gray-600 whitespace-nowrap">{fmtDate(row.created_at)}</td>
                <td className="px-3 py-2 font-mono text-xs">{row.booking_id}</td>
                <td className="px-3 py-2 max-w-[140px]">
                  <span className="block font-medium text-gray-900 truncate" title={row.client_name || row.customer_name}>
                    {row.client_name || row.customer_name || '—'}
                  </span>
                  {row.client_type && (
                    <span className="text-[10px] uppercase text-gray-500 font-semibold">
                      {row.client_type === 'agent' ? 'Agent' : 'B2C'}
                    </span>
                  )}
                </td>
                <td className="px-3 py-2 font-mono text-xs text-gray-700 max-w-[120px] truncate" title={row.supplier_booking_id}>{row.supplier_booking_id || '—'}</td>
                <td className="px-3 py-2 font-mono">{row.pnr || '—'}</td>
                <td className="px-3 py-2">{row.route || '—'}</td>
                <td className="px-3 py-2 text-center">{row.pax_count ?? '—'}</td>
                <td className="px-3 py-2 font-semibold whitespace-nowrap">₹{Number(row.total_amount || 0).toLocaleString()}</td>
                <td className="px-3 py-2 text-xs">
                  <span className="block">{row.payment_method || '—'}</span>
                  {row.payment_status && <span className="text-gray-500">{row.payment_status}</span>}
                </td>
                <td className="px-3 py-2">
                  <SupplierStatusBadge supplierStatus={{
                    raw: row.supplier_status_raw,
                    canonical: row.supplier_canonical,
                    bucket: row.supplier_status_bucket,
                    label: row.supplier_status_label,
                  }} />
                  <span className="block text-[10px] text-gray-500 mt-0.5">
                    ACF: {row.acf_status}
                    {row.acf_status_stale && row.acf_booking_status && (
                      <span
                        className="ml-1 text-amber-700 font-semibold"
                        title={`Stored ACF status is stale; supplier implies ${row.acf_booking_status}`}
                      >
                        (stale → {row.acf_booking_status})
                      </span>
                    )}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <button type="button" onClick={() => openDetail(row.booking_id)} className="text-blue-600 text-sm font-medium flex items-center gap-1">
                    <Eye className="w-4 h-4" /> Open
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <button type="button" disabled={page <= 1} onClick={() => setPage((p) => p - 1)} className="px-3 py-1 border rounded disabled:opacity-50">Prev</button>
          <span className="text-sm text-gray-600 py-1">Page {page} / {pages}</span>
          <button type="button" disabled={page >= pages} onClick={() => setPage((p) => p + 1)} className="px-3 py-1 border rounded disabled:opacity-50">Next</button>
        </div>
      )}

      <BookingDetailDrawer
        hub={hub}
        open={drawerOpen}
        onClose={() => { setDrawerOpen(false); setHub(null); }}
        onHubRefresh={() => bid && openDetail(bid)}
      >
        {actionButtons}
      </BookingDetailDrawer>

      <TripjackCancelModal open={tjCancelOpen} onClose={() => setTjCancelOpen(false)} bookingId={tjCancelBookingId}
        onCancelled={() => { setTjCancelOpen(false); setDrawerOpen(false); fetchList(); }} />
      <RiyaCancelModal open={riyaCancelOpen} onClose={() => setRiyaCancelOpen(false)} bookingId={bid} booking={riyaCancelBooking || {}} mode={riyaCancelMode}
        onCancelled={() => { setRiyaCancelOpen(false); fetchList(); }} />
      <RiyaRescheduleModal open={!!riyaRescheduleBooking} onClose={() => setRiyaRescheduleBooking(null)} booking={riyaRescheduleBooking || {}} />
      <RiyaSSRModal open={!!riyaSSRBooking} onClose={() => setRiyaSSRBooking(null)} booking={riyaSSRBooking || {}} />
      <ConfirmHoldModal open={confirmHoldOpen} onClose={() => setConfirmHoldOpen(false)} booking={confirmHoldBooking || {}}
        onConfirmed={() => { setConfirmHoldOpen(false); openDetail(bid); fetchList(); }} />
    </div>
  );
}
